import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import {
  REGIONS,
  type ManagePartyAliasRequest,
  type ManagePartyAliasResponse,
  type PartyAlias,
  type Region,
  type ResolvedParty,
  type ResolvePartyResponse,
  type VendorAddress,
  type VendorReference,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { fetchResolveParty, managePartyAlias } from '../api/party-aliases';
import { setPartyRegion } from '../api/party-region';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { requireSurface } from '../routes/surfaces';

/**
 * Party aliases — FR-29, NFR-13. CLM-56, D-B8; closes B-13.
 *
 * ## What this surface is
 *
 * The party-alias list **is** the FR-29 control. Substitution is string
 * replacement of known values (§4A.1), so what a human approves here is exactly
 * what leaves the tenant tokenised on the next pseudonymisation run. Three
 * regions, each a real read or a real write:
 *
 *  1. **Awaiting approval** — the §4A.5 loop's work list: variants the leak
 *     detector persisted as `Detected`, unconfirmed rows. Approve or Reject
 *     (reason required).
 *  2. **Look up a party** — `resolve-party` over the canonical name, the party
 *     key and every alias, all states. Punctuation-tolerant since CLM-75, so a
 *     freshly seeded party with no alias is found by its key or a loose name.
 *  3. **One party's aliases** — the full history for one party, with Retire and
 *     the surface's primary action, **Add party alias**.
 *
 * ## Two rules the markup obeys
 *
 * *The primary actions render whether or not the first API call succeeded.* In
 * prod the API has never been deployed (B-33), so the queue read fails with a
 * network error; the search form and the add form still paint, and the failure
 * renders honestly as `ErrorNotice`. A `501` names both endpoints.
 *
 * *Nothing is hidden by role.* A contributor sees the queue read-only and gets
 * the honest `forbidden` notice on click — the gateway is the boundary, not a
 * hidden button (NFR-06). The one hidden control is Retire on a `Qfly` row,
 * hidden because the action is *impossible* (the server answers 422), not
 * because of who is looking.
 *
 * Every write re-fetches through `reloadNonce` rather than patching rows: the
 * post-image is the server's, and `substitution_affected` is what the status
 * line reports.
 *
 * Copy says **"party alias"** everywhere — "alias" alone clashes with the
 * clause-search aliases of Phase 5.
 */

type Panel<TResponse> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly response: TResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

interface WriteProblem {
  readonly kind: ApiProblemKind;
  readonly detail: string | null;
  readonly correlation_id: string | null;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
}

interface WriteOutcome {
  readonly action: ManagePartyAliasRequest['action'];
  readonly alias_text: string;
  readonly response: ManagePartyAliasResponse;
}

/** One pending row, flattened for the queue table. */
interface PendingRow {
  readonly party: ResolvedParty;
  readonly alias: PartyAlias;
}

const QUEUE_LIMIT = 20;
const ALL_STATES = ['active', 'pending', 'retired', 'rejected'] as const;

function toProblem(error: unknown): Extract<Panel<never>, { kind: 'problem' }> {
  return {
    kind: 'problem',
    problem: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

function toWriteProblem(error: unknown): WriteProblem {
  const body = error instanceof ApiError ? (error.body as { field_errors?: unknown } | null) : null;
  const fieldErrors = Array.isArray(body?.field_errors)
    ? (body.field_errors as { path?: unknown; message?: unknown }[])
        .filter((entry) => typeof entry.path === 'string' && typeof entry.message === 'string')
        .map((entry) => ({ path: entry.path as string, message: entry.message as string }))
    : [];
  return {
    kind: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
    field_errors: fieldErrors,
  };
}

function rowId(prefix: string, partyKey: string, index: number): string {
  return `${prefix}-${partyKey}-${String(index)}`;
}

function lengthCell(alias: PartyAlias) {
  return (
    <>
      {alias.alias_text.length}
      {alias.alias_text.length < 5 ? (
        <>
          {' '}
          <span className="tag out">short — explicit confirmation</span>
        </>
      ) : null}
    </>
  );
}

/** CLM-114 (R-114-1) — an alias the vendor-list import wrote (or confirmed) says which upload, beside its source. */
function sourceCell(alias: PartyAlias) {
  if (alias.import_batch_id === null) return alias.source;
  return (
    <>
      {alias.source} <span className="tag up">Imported · batch #{alias.import_batch_id}</span>
    </>
  );
}

function addressLine(address: VendorAddress): string {
  return [
    address.address_line_1,
    address.address_line_2,
    address.city,
    address.post_code,
    address.county,
    address.state_code,
    address.country_code,
  ]
    .filter((part): part is string => part !== null)
    .join(', ');
}

/**
 * CLM-114 (R-114-5) — the party's vendor-list references: which cloudEQ
 * company's card names it, the vendor number, and the address that card
 * carried. A cache of the Business Central record, never authoritative (DR-12,
 * rule 8): the label says so. An included individual keeps the name only, so
 * there is never an address to show. Mounted only when the party has a
 * reference, so the entity registry is read only then.
 */
function VendorListPanel({ partyKey, references }: { readonly partyKey: string; readonly references: readonly VendorReference[] }) {
  const entities = useCloudeqEntities();
  const nameOf = (key: string): string => entities.find((entity) => entity.entity_key === key)?.display_name ?? key;
  const columns: readonly DataTableColumn<VendorReference>[] = [
    { id: 'entity', header: 'cloudEQ company', cell: (reference) => nameOf(reference.cloudeq_entity_key) },
    { id: 'vendor_no', header: 'Vendor no.', cell: (reference) => <code>{reference.vendor_no}</code> },
    {
      id: 'address',
      header: 'Address',
      cell: (reference) =>
        reference.is_individual ? (
          <span className="mut">An individual — the name only, never an address</span>
        ) : reference.address === null ? (
          <span className="mut">No address in the sheet</span>
        ) : (
          addressLine(reference.address)
        ),
    },
    {
      id: 'imported',
      header: 'Source',
      cell: (reference) => (
        <>
          from the vendor list, imported <time dateTime={reference.imported_utc}>{reference.imported_utc.slice(0, 10)}</time>{' '}
          — Dynamics is the record
          <br />
          <span className="mut">
            batch #{reference.first_import_batch_id}
            {reference.last_import_batch_id === reference.first_import_batch_id
              ? ''
              : `, last changed in batch #${reference.last_import_batch_id}`}
          </span>
        </>
      ),
    },
  ];
  return (
    <section aria-labelledby={`pa-vendor-list-${partyKey}`}>
      <h4 id={`pa-vendor-list-${partyKey}`}>Vendor list</h4>
      <DataTable
        caption={`Vendor-list references of ${partyKey}`}
        columns={columns}
        rows={references}
        rowKey={(reference) => `${reference.cloudeq_entity_key}|${reference.vendor_no}`}
        empty={<p>No vendor list names this party.</p>}
      />
    </section>
  );
}

function stateTag(state: PartyAlias['state']) {
  const tone = state === 'active' ? 'up' : state === 'pending' ? 'bad' : 'out';
  return <span className={`tag ${tone}`}>{state}</span>;
}

/* -------------------------------------------------------------------------- */
/* Reason panel — shared by Reject and Retire                                 */
/* -------------------------------------------------------------------------- */

function ReasonPanel({
  verb,
  aliasText,
  busy,
  onConfirm,
  onCancel,
}: {
  readonly verb: 'rejecting' | 'retiring';
  readonly aliasText: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const label = verb === 'rejecting' ? 'Confirm rejection' : 'Confirm retirement';
  return (
    <div className="vform">
      <div className="field">
        <label htmlFor={`pa-reason-${verb}`}>Reason</label>
        <textarea
          id={`pa-reason-${verb}`}
          aria-label={`Reason for ${verb} ${aliasText}`}
          value={reason}
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <div className="vacts">
        <button
          type="button"
          className="btn pri"
          disabled={busy || reason.trim() === ''}
          onClick={() => onConfirm(reason.trim())}
        >
          {label}
        </button>
        <button type="button" className="btn qt" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * CLM-87 — the party's region as a badge and the admin override beside it
 * (D-87-10). Never disabled by role: the API is the gate (CLM-82); a 403 renders
 * the row's problem copy and the select stays usable. `Unassigned` = `null`.
 */
function regionTitle(party: ResolvedParty['party']): string {
  if (party.region_source === 'admin') return 'Set by an admin';
  if (party.region_source === 'verified_contract') return 'Set from the first verified contract';
  return 'No verified counterparty country yet — set it here or verify a contract';
}

/**
 * CLM-99 — the party's Dynamics billing client id and whether it makes the
 * client billable. Read-only, unlike region: D-99-5 — there is no admin
 * override for this story, since the value is asserted correct-by-construction
 * from Dynamics via Qfly, with no ambiguity to correct. A client with no id
 * shows an explicit "Not billable" state, never a blank cell (R-99-1 item 4).
 */
function billingClientIdTitle(party: ResolvedParty['party']): string {
  if (party.billing_client_id === null) return 'No Dynamics billing client id on record — not billable';
  return 'Read from Qfly by party-sync';
}

function BillingClientIdCell({ party }: { readonly party: ResolvedParty['party'] }) {
  if (party.billing_client_id === null) {
    return (
      <span className="tag" title={billingClientIdTitle(party)}>
        Not billable
      </span>
    );
  }
  return (
    <span title={billingClientIdTitle(party)}>
      <code>{party.billing_client_id}</code> <span className="tag up">Billable</span>
    </span>
  );
}

function RegionCell({
  party,
  busy,
  onSet,
}: {
  readonly party: ResolvedParty['party'];
  readonly busy: boolean;
  readonly onSet: (region: Region | null) => void;
}) {
  return (
    <span className="region-cell">
      <span className="tag" title={regionTitle(party)}>
        {party.region ?? 'Unassigned'}
      </span>{' '}
      <select
        aria-label={`Region for ${party.cached_display_name}`}
        value={party.region ?? 'unassigned'}
        disabled={busy}
        onChange={(event) => {
          const value = event.target.value;
          onSet(value === 'unassigned' ? null : (value as Region));
        }}
      >
        <option value="unassigned">Unassigned</option>
        {REGIONS.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </span>
  );
}

function WriteNotice({
  problem,
  onReload,
}: {
  readonly problem: WriteProblem;
  readonly onReload: () => void;
}) {
  if (problem.kind === 'conflict') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>Another reviewer settled this party alias</strong>
        </p>
        {problem.field_errors.map((entry) => (
          <p key={entry.path} className="mut">
            {entry.message}
          </p>
        ))}
        <button type="button" className="btn" onClick={onReload}>
          Reload
        </button>
      </div>
    );
  }
  if (problem.kind === 'not_found') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>This party alias is no longer on this party — reload</strong>
        </p>
        <button type="button" className="btn" onClick={onReload}>
          Reload
        </button>
      </div>
    );
  }
  if (problem.kind === 'validation') {
    return (
      <div className="banner bad" role="alert">
        <p className="notice-head">
          <strong>The party alias write was refused</strong>
        </p>
        {problem.field_errors.length === 0 ? (
          <p>{problem.detail}</p>
        ) : (
          problem.field_errors.map((entry) => (
            <p key={entry.path} className="ferr">
              <code>{entry.path}</code>: {entry.message}
            </p>
          ))
        )}
      </div>
    );
  }
  return <ErrorNotice kind={problem.kind} detail={problem.detail} correlation_id={problem.correlation_id} />;
}

function OutcomeLine({ outcome }: { readonly outcome: WriteOutcome }) {
  const verb =
    outcome.action === 'approve'
      ? 'Approved'
      : outcome.action === 'reject'
        ? 'Rejected'
        : outcome.action === 'retire'
          ? 'Retired'
          : 'Added';
  return (
    <p role="status" className="mut">
      {verb} <code>{outcome.alias_text}</code> —{' '}
      {outcome.response.substitution_affected
        ? 'the next pseudonymisation run compiles a new map version'
        : 'nothing changes in the substitution map'}
      . Audit row <code>{outcome.response.audit.audit_id}</code>.
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/* The surface                                                                 */
/* -------------------------------------------------------------------------- */

export function PartyAliases() {
  const surface = requireSurface('alias');

  const [queue, setQueue] = useState<Panel<ResolvePartyResponse>>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [openReason, setOpenReason] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [writeProblem, setWriteProblem] = useState<{ readonly row: string; readonly problem: WriteProblem } | null>(null);
  const [outcome, setOutcome] = useState<WriteOutcome | null>(null);

  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [results, setResults] = useState<Panel<ResolvePartyResponse> | null>(null);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [party, setParty] = useState<Panel<ResolvePartyResponse> | null>(null);
  const [newAlias, setNewAlias] = useState('');
  const [newNote, setNewNote] = useState('');

  const queueAbort = useRef<AbortController | null>(null);

  const reload = useCallback(() => {
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  /* --- Region 1: the queue ------------------------------------------------ */
  useEffect(() => {
    const controller = new AbortController();
    queueAbort.current?.abort();
    queueAbort.current = controller;
    setQueue((previous) => (previous.kind === 'ready' ? previous : { kind: 'loading' }));
    void (async () => {
      try {
        const response = await fetchResolveParty(
          {
            include_aliases: true,
            alias_state: ['pending'],
            require_alias_match: true,
            page: { limit: QUEUE_LIMIT },
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setQueue({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setQueue(toProblem(error));
      }
    })();
    return () => {
      controller.abort();
    };
  }, [reloadNonce]);

  /* --- Region 2: search --------------------------------------------------- */
  useEffect(() => {
    if (searchTerm === null) return;
    const controller = new AbortController();
    setResults({ kind: 'loading' });
    void (async () => {
      try {
        const response = await fetchResolveParty(
          {
            search: searchTerm,
            include_inactive: true,
            include_aliases: true,
            alias_state: [...ALL_STATES],
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setResults({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setResults(toProblem(error));
      }
    })();
    return () => {
      controller.abort();
    };
  }, [searchTerm, reloadNonce]);

  /* --- Region 3: one party ------------------------------------------------ */
  useEffect(() => {
    if (selectedKey === null) return;
    const controller = new AbortController();
    setParty((previous) => (previous?.kind === 'ready' ? previous : { kind: 'loading' }));
    void (async () => {
      try {
        const response = await fetchResolveParty(
          {
            party_key: selectedKey,
            include_inactive: true,
            include_aliases: true,
            alias_state: [...ALL_STATES],
            /* CLM-114 (R-114-5) — the "Vendor list" panel's references. */
            include_references: true,
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setParty({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setParty(toProblem(error));
      }
    })();
    return () => {
      controller.abort();
    };
  }, [selectedKey, reloadNonce]);

  /* --- the one write path ------------------------------------------------- */
  const write = useCallback(
    async (row: string, request: ManagePartyAliasRequest, returnFocusTo: string | null) => {
      setBusyRow(row);
      setWriteProblem(null);
      setOutcome(null);
      try {
        const response = await managePartyAlias(request);
        setOutcome({ action: request.action, alias_text: request.alias_text, response });
        setOpenReason(null);
        reload();
      } catch (error) {
        setWriteProblem({ row, problem: toWriteProblem(error) });
      } finally {
        setBusyRow(null);
        if (returnFocusTo !== null) document.getElementById(returnFocusTo)?.focus();
      }
    },
    [reload],
  );

  /* CLM-87 — the region override: one POST, then the lookup re-fetches (D-87-10). */
  const writeRegion = useCallback(
    async (partyKey: string, region: Region | null) => {
      const row = `pa-region-${partyKey}`;
      setBusyRow(row);
      setWriteProblem(null);
      try {
        await setPartyRegion({ party_key: partyKey, region });
        reload();
      } catch (error) {
        setWriteProblem({ row, problem: toWriteProblem(error) });
      } finally {
        setBusyRow(null);
      }
    },
    [reload],
  );

  const closeReason = useCallback((returnFocusTo: string) => {
    setOpenReason(null);
    document.getElementById(returnFocusTo)?.focus();
  }, []);

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const term = searchText.trim();
    if (term === '') return;
    setSearchTerm(term);
  };

  const onAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedKey === null) return;
    const text = newAlias.trim();
    if (text === '') return;
    const note = newNote.trim();
    void write(
      `pa-add-${selectedKey}`,
      { action: 'propose', party_key: selectedKey, alias_text: text, ...(note === '' ? {} : { note }) },
      null,
    ).then(() => {
      setNewAlias('');
      setNewNote('');
    });
  };

  /* --- derived rows ------------------------------------------------------- */
  const pendingRows: readonly PendingRow[] =
    queue.kind === 'ready'
      ? queue.response.parties.flatMap((entry) =>
          (entry.aliases ?? []).filter((alias) => alias.state === 'pending').map((alias) => ({ party: entry, alias })),
        )
      : [];

  const selected: ResolvedParty | null =
    party?.kind === 'ready' ? (party.response.parties.find((entry) => entry.party.party_key === selectedKey) ?? null) : null;

  const pendingColumns: readonly DataTableColumn<PendingRow>[] = [
    {
      id: 'party',
      header: 'Party',
      cell: (row) => (
        <>
          {row.party.party.cached_display_name}
          <br />
          <code>{row.party.party.party_key}</code>
        </>
      ),
    },
    { id: 'alias', header: 'Party alias', cell: (row) => row.alias.alias_text },
    { id: 'length', header: 'Length', cell: (row) => lengthCell(row.alias), cellClassName: 'dt-num' },
    { id: 'source', header: 'Source', cell: (row) => sourceCell(row.alias) },
    { id: 'detected', header: 'Detected on', cell: (row) => row.alias.created_utc },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => {
        const index = pendingRows.indexOf(row);
        const id = rowId('pa-row', row.party.party.party_key, index);
        const rejectId = rowId('pa-reject', row.party.party.party_key, index);
        const busy = busyRow === id;
        return (
          <div aria-busy={busy}>
            <div className="vacts">
              <button
                type="button"
                className="btn pri"
                disabled={busy}
                aria-label={`Approve party alias ${row.alias.alias_text} for ${row.party.party.party_key}`}
                onClick={() =>
                  void write(
                    id,
                    { action: 'approve', party_key: row.party.party.party_key, alias_text: row.alias.alias_text },
                    null,
                  )
                }
              >
                Approve
              </button>
              <button
                type="button"
                id={rejectId}
                className="btn"
                disabled={busy}
                aria-expanded={openReason === id}
                aria-label={`Reject party alias ${row.alias.alias_text} for ${row.party.party.party_key}`}
                onClick={() => setOpenReason(openReason === id ? null : id)}
              >
                Reject
              </button>
            </div>
            {openReason === id ? (
              <ReasonPanel
                verb="rejecting"
                aliasText={row.alias.alias_text}
                busy={busy}
                onConfirm={(reason) =>
                  void write(
                    id,
                    { action: 'reject', party_key: row.party.party.party_key, alias_text: row.alias.alias_text, reason },
                    rejectId,
                  )
                }
                onCancel={() => closeReason(rejectId)}
              />
            ) : null}
            {writeProblem?.row === id ? <WriteNotice problem={writeProblem.problem} onReload={reload} /> : null}
          </div>
        );
      },
    },
  ];

  const resultColumns: readonly DataTableColumn<ResolvedParty>[] = [
    {
      id: 'party',
      header: 'Party',
      cell: (entry) => (
        <>
          {entry.party.cached_display_name}
          <br />
          <code>{entry.party.party_key}</code>
        </>
      ),
    },
    { id: 'type', header: 'Type', cell: (entry) => entry.party.party_type },
    { id: 'sor', header: 'System of record', cell: (entry) => entry.party.sor_system },
    {
      id: 'region',
      header: 'Region',
      cell: (entry) => {
        const row = `pa-region-${entry.party.party_key}`;
        return (
          <div>
            <RegionCell
              party={entry.party}
              busy={busyRow === row}
              onSet={(region) => {
                void writeRegion(entry.party.party_key, region);
              }}
            />
            {writeProblem?.row === row ? <WriteNotice problem={writeProblem.problem} onReload={reload} /> : null}
          </div>
        );
      },
    },
    {
      id: 'billing_client_id',
      header: 'Dynamics billing id',
      cell: (entry) => <BillingClientIdCell party={entry.party} />,
    },
    { id: 'active', header: 'Active', cell: (entry) => (entry.party.is_active ? 'yes' : 'no') },
    {
      id: 'restricted',
      header: 'Restricted',
      cell: (entry) => (entry.party.processing_restricted ? <span className="tag bad">restricted</span> : 'no'),
    },
  ];

  const aliasColumns: readonly DataTableColumn<PartyAlias>[] = [
    { id: 'alias', header: 'Party alias', cell: (alias) => alias.alias_text },
    { id: 'length', header: 'Length', cell: lengthCell, cellClassName: 'dt-num' },
    { id: 'source', header: 'Source', cell: sourceCell },
    { id: 'state', header: 'State', cell: (alias) => stateTag(alias.state) },
    {
      id: 'confirmed',
      header: 'Confirmed by',
      cell: (alias) =>
        alias.confirmed_by_upn === null ? (
          <span className="mut">—</span>
        ) : (
          <>
            {alias.confirmed_by_upn}
            <br />
            <span className="mut">{alias.confirmed_utc}</span>
          </>
        ),
    },
    { id: 'note', header: 'Note', cell: (alias) => alias.decision_note ?? <span className="mut">—</span> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (alias) => {
        if (selected === null) return null;
        const partyKey = selected.party.party_key;
        const index = (selected.aliases ?? []).indexOf(alias);
        const id = rowId('pa-alias', partyKey, index);
        const retireId = rowId('pa-retire', partyKey, index);
        const busy = busyRow === id;
        if (alias.source === 'Qfly') return <span className="mut">canonical name</span>;
        if (alias.state !== 'active') return <span className="mut">—</span>;
        return (
          <div aria-busy={busy}>
            <button
              type="button"
              id={retireId}
              className="btn"
              disabled={busy}
              aria-expanded={openReason === id}
              aria-label={`Retire party alias ${alias.alias_text} for ${partyKey}`}
              onClick={() => setOpenReason(openReason === id ? null : id)}
            >
              Retire
            </button>
            {openReason === id ? (
              <ReasonPanel
                verb="retiring"
                aliasText={alias.alias_text}
                busy={busy}
                onConfirm={(reason) =>
                  void write(id, { action: 'retire', party_key: partyKey, alias_text: alias.alias_text, reason }, retireId)
                }
                onCancel={() => closeReason(retireId)}
              />
            ) : null}
            {writeProblem?.row === id ? <WriteNotice problem={writeProblem.problem} onReload={reload} /> : null}
          </div>
        );
      },
    },
  ];

  const notBuilt = (
    <p className="mut">
      <code>GET /v1/parties/resolve</code> and <code>POST /v1/parties/aliases/actions</code> are not built in the gateway
      you are talking to; an empty queue here would be a false claim about FR-29.
    </p>
  );

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      {outcome !== null ? <OutcomeLine outcome={outcome} /> : null}

      {/* ---- Region 1 ------------------------------------------------------ */}
      <div className="card">
        <h3>Awaiting approval</h3>
        <p>
          Variants the §4A.5 leak detector found surviving substitution, persisted as unconfirmed{' '}
          <code>Detected</code> party aliases. Nothing here is substituted until a person approves it; a
          rejection keeps the row so the same variant is not queued again. Every decision is one audit row
          (NFR-13).
        </p>
        {queue.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading party aliases…
          </p>
        ) : null}
        {queue.kind === 'problem' ? (
          <>
            <ErrorNotice kind={queue.problem} detail={queue.detail} correlation_id={queue.correlation_id} />
            {queue.problem === 'not_implemented' ? notBuilt : null}
          </>
        ) : null}
        {queue.kind === 'ready' ? (
          <DataTable
            caption="Party aliases awaiting approval"
            columns={pendingColumns}
            rows={pendingRows}
            rowKey={(row) => `${row.party.party.party_key}|${row.alias.alias_text}`}
            busy={busyRow !== null}
            empty={
              <p>
                No party aliases are waiting for approval. The leak detector queues a variant here when a party
                name survives substitution (§4A.5).
              </p>
            }
          />
        ) : null}
      </div>

      {/* ---- Region 2 ------------------------------------------------------ */}
      <form className="card" role="search" onSubmit={onSearch}>
        <h3>Look up a party</h3>
        <p>
          Searches the canonical name, the party key (for example <code>AGM</code>) and every known
          party alias, active or not. Punctuation and case are ignored, so <em>m booth</em> finds
          M. Booth &amp; Associates. Parties are corpus reference data; the contracts listed for one
          are only those you may see.
        </p>
        <div className="sbar">
          <div className="field grow">
            <label htmlFor="pa-search">Party name, alias or key</label>
            <input
              id="pa-search"
              type="text"
              autoComplete="off"
              aria-label="Party name, alias or key"
              placeholder="e.g. AGM or M Booth"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <button type="submit" className="btn pri">
            Search parties
          </button>
        </div>
        {results?.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading party aliases…
          </p>
        ) : null}
        {results?.kind === 'problem' ? (
          <>
            <ErrorNotice kind={results.problem} detail={results.detail} correlation_id={results.correlation_id} />
            {results.problem === 'not_implemented' ? notBuilt : null}
          </>
        ) : null}
        {results?.kind === 'ready' ? (
          <DataTable
            caption="Parties matching the search"
            columns={resultColumns}
            rows={results.response.parties}
            rowKey={(entry) => entry.party.party_key}
            isCurrent={(entry) => entry.party.party_key === selectedKey}
            empty={<p>No party matches that name or party alias.</p>}
            activate={{
              header: 'Party aliases',
              label: (entry) => `Show party aliases for ${entry.party.party_key}`,
              onActivate: (entry) => setSelectedKey(entry.party.party_key),
              buttonId: (entry) => `pa-show-${entry.party.party_key}`,
            }}
          />
        ) : null}
      </form>

      {/* ---- Region 3 ------------------------------------------------------ */}
      {selectedKey !== null ? (
        <div className="card">
          {selected !== null ? (
            <>
              <h3>{selected.party.cached_display_name}</h3>
              <p className="mut">
                Substituted as <code>{selected.pseudonym_token}</code> · {selected.in_scope_contract_keys.length}{' '}
                contracts you can see
              </p>
            </>
          ) : (
            <h3>
              Party <code>{selectedKey}</code>
            </h3>
          )}

          <form onSubmit={onAdd}>
            <h4>Add party alias</h4>
            <div className="field">
              <label htmlFor="pa-new-alias">New party alias</label>
              <input
                id="pa-new-alias"
                type="text"
                aria-label="New party alias"
                value={newAlias}
                disabled={busyRow === `pa-add-${selectedKey}`}
                onChange={(event) => setNewAlias(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pa-new-note">Note (optional)</label>
              <textarea
                id="pa-new-note"
                aria-label="Note (optional)"
                value={newNote}
                disabled={busyRow === `pa-add-${selectedKey}`}
                onChange={(event) => setNewNote(event.target.value)}
              />
            </div>
            <div className="vacts">
              <button type="submit" className="btn pri" disabled={busyRow === `pa-add-${selectedKey}` || newAlias.trim() === ''}>
                Add party alias
              </button>
            </div>
            <p className="mut">
              A party alias you add is confirmed by you at once and applies to the next pseudonymisation run (FR-29).
            </p>
            {writeProblem?.row === `pa-add-${selectedKey}` ? (
              <WriteNotice problem={writeProblem.problem} onReload={reload} />
            ) : null}
          </form>

          {party?.kind === 'loading' ? (
            <p role="status" aria-busy="true">
              Reading party aliases…
            </p>
          ) : null}
          {party?.kind === 'problem' ? (
            <>
              <ErrorNotice kind={party.problem} detail={party.detail} correlation_id={party.correlation_id} />
              {party.problem === 'not_implemented' ? notBuilt : null}
            </>
          ) : null}
          {selected !== null ? (
            <DataTable
              caption={`Party aliases of ${selected.party.party_key}`}
              columns={aliasColumns}
              rows={selected.aliases ?? []}
              rowKey={(alias) => alias.alias_text}
              busy={busyRow !== null}
              empty={<p>This party has no recorded party alias beyond its canonical name.</p>}
            />
          ) : null}
          {selected !== null && selected.references !== null && selected.references.length > 0 ? (
            <VendorListPanel partyKey={selected.party.party_key} references={selected.references} />
          ) : null}
        </div>
      ) : null}
    </>
  );
}
